import React, { useState, useEffect } from "react";
import {
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import ActionButton from "../../components/ActionButton";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { authServiceClient as axios } from "../../AxiosClient";
import moment from "moment";
import { setLicence } from "../../redux/actions/universalActions";
import LicenceInput from "../../components/licence/LicenceInput";
import { Alert } from "@material-ui/lab";
import { taxServiceClient, authServiceClient } from "../../AxiosClient";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import DeleteIcon from "@material-ui/icons/Delete";
import psl from "psl";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: 15,
    display: "flex",
  },
  container: {
    height: "100%",
    width: "100%",
  },
  flexContainer: {
    display: "flex",
  },
  alert: {
    width: "50%",
    margin: 5,
  },
  accordionRoot: {
    border: "1px solid rgba(0, 0, 0, .125)",
    boxShadow: "none",
    "&:not(:last-child)": {
      borderBottom: 0,
    },
    "&:before": {
      display: "none",
    },
    "&$expanded": {
      margin: "auto",
    },
  },
  accordionSummaryRoot: {
    backgroundColor: "rgba(0, 0, 0, .03)",
    borderBottom: "1px solid rgba(0, 0, 0, .125)",
    marginBottom: -1,
    minHeight: 56,
    "&$expanded": {
      minHeight: 56,
    },
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: "33.33%",
    flexShrink: 0,
  },
  keyColumn: {
    maxWidth: 100,
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  descriptionColumn: {
    maxWidth: 150,
  },
}));
function LicenceSettings() {
  const [saving, setSaving] = useState(false);
  const [key, setKey] = useState("");
  const [licenceValid, setLicenceValid] = useState(false);
  const [allLicences, setAllLicences] = useState([]);
  const [activeConcurrentUsers, setActiveConcurrentUsers] = useState(0);
  const [concurrentUsersRegistered, setConcurrentUsersRegistered] = useState(0);
  const [activeNamedUsers, setActiveNamedUsers] = useState(0);
  const [activeLimitedUsers, setActiveLimitedUsers] = useState(0);
  const dispatch = useDispatch();
  const [confirmLicenseDialogOpen, setConfirmLicenseDialogOpen] = useState(
    false
  );
  const [confirmDeleteDialogOpen, setConfirmDeleteDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const organization = useSelector((state) => {
    if (state.user.currentUser) {
      return state.user.currentUser.organization.organizationId;
    }
    return "";
  });
  const licence = useSelector((state) => {
    return state.setting.licence;
  });

  useEffect(() => {
    // get active users on mount so we get a more up-to-date
    // picture of active users vs when the user logged in and we
    // fetched the licence
    // let parsed = psl.parse(window.location.hostname);
    // let organization = parsed.subdomain;
    let organization = "demo";
    taxServiceClient
      .get(`/user/active/${organization}`)
      .then((res) => {
        setActiveConcurrentUsers(res.data && res.data.concurrentUsersActive ? res.data.concurrentUsersActive : 0);
        setActiveNamedUsers(res.data && res.data.namedUsersActive ? res.data.namedUsersActive : 0);
        setActiveLimitedUsers(res.data && res.data.limitedUsersActive ? res.data.limitedUsersActive : 0);
        setConcurrentUsersRegistered(res.data && res.data.concurrentUsersRegistered ? res.data.concurrentUsersRegistered : 0)

      })
      .catch((err) => {
        enqueueSnackbar(t("error_occurred_fetching_active_users"), {
          variant: "error",
        });
      });
    getAllLicences();
  }, []);

  const getAllLicences = () => {
    authServiceClient
      .get("/licence/current/all")
      .then((res) => {
        setAllLicences(res.data);
      })
      .catch((err) => {
        enqueueSnackbar(t("error_occurred_fetching_licences"), {
          variant: "error",
        });
      });
  };

  const deleteLicence = () => {
    axios
      .delete(`/licence/${toDelete.id}`)
      .then(() => {
        enqueueSnackbar(t("licence_deleted_snackbar"), { variant: "success" });
        setConfirmDeleteDialogOpen(false);
        authServiceClient.get("/licence/current").then((res) => {
          dispatch(setLicence(res.data));
        });
        getAllLicences();
      })
      .catch(() => {
        enqueueSnackbar(t("error_occurred_generic"), { variant: "error" });
      });
  };

  const licenseOrganization = () => {
    axios
      .post(`/licence/${organization}/license`, {
        key,
      })
      .then((res) => {
        authServiceClient.get("/licence/current").then((res) => {
          dispatch(setLicence(res.data));
        });
        setConfirmLicenseDialogOpen(false);
        getAllLicences();
        setKey("");
      })
      .catch((err) => {
        enqueueSnackbar(t("error_occurred_generic"), { variant: "error" });
      });
  };

  const getAlerts = () => {
    let alerts = [];
    if (licence.usedDocuments >= licence.documents * 0.9) {
      if (licence.usedDocuments >= licence.documents) {
        alerts.push(
          <Alert severity="error" className={classes.alert}>
            {t("licence_document_limit_reached")}
          </Alert>
        );
      } else {
        alerts.push(
          <Alert severity="warning" className={classes.alert}>
            {t("licence_close_to_document_limit")}
          </Alert>
        );
      }
    }
    if (licence.usedUsers >= licence.namedUsers) {
      alerts.push(
        <Alert severity="error" className={classes.alert}>
          {t("licence_user_limit_reached")}
        </Alert>
      );
    }
    if (alerts.length === 0) {
      alerts = [
        <Alert className={classes.alert}>{t("licence_valid_alert")}</Alert>,
      ];
    }
    return alerts;
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h5" style={{ marginRight: 10 }}>
          {t("licence_settings_header")}
        </Typography>
      </div>
      <>
        <div style={{ marginBottom: 10 }}>
          <Typography variant="h6">{t("new_licence_header")}</Typography>
          <Typography variant="body2" gutterBottom>
            {t("new_licence_subheader")}
          </Typography>
          <div style={{ display: "flex" }}>
            <div style={{ width: "33%" }}>
              <LicenceInput
                setKey={(key) => setKey(key)}
                licenceKey={key}
                setLicenceValid={(valid) => setLicenceValid(valid)}
              />
            </div>
            <ActionButton
              variant="contained"
              color="primary"
              disabled={!licenceValid}
              onClick={() => setConfirmLicenseDialogOpen(true)}
              loading={saving}
              style={{ marginLeft: 15, marginTop: 12 }}
            >
              {t("global_save")}
            </ActionButton>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column" }}>
          <Typography variant="h6">{t("licence_details_header")}</Typography>
          <Typography variant="body2" gutterBottom>
            {t("licence_details_subheader")}
          </Typography>
          {licence ? (
            <div style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {getAlerts()}
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h5" color="textSecondary">
                      {t("online_users")}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h4" style={{ marginBottom: 10 }}>
                      {activeNamedUsers} / {licence.namedUsers}
                    </Typography>
                    <Typography variant="h5" color="textSecondary">
                      {t("global_user_plural")}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h4" style={{ marginBottom: 10 }}>
                      {activeConcurrentUsers} / {licence.concurrentUsers}
                    </Typography>
                    <Typography variant="h5" color="textSecondary">
                      {t("concurrent_users")}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h4" style={{ marginBottom: 10 }}>
                      {activeLimitedUsers} / {licence.limitedUsers}
                    </Typography>
                    <Typography variant="h5" color="textSecondary">
                      {t("limited_user_plural")}
                    </Typography>
                  </CardContent>
                </Card>
              </div>
              
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h5" color="textSecondary">
                      {t("registered_users")}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h4" style={{ marginBottom: 10 }}>
                      {licence.usedUsers} / {licence.namedUsers}
                    </Typography>
                    <Typography variant="h5" color="textSecondary">
                      {t("global_user_plural")}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h4" style={{ marginBottom: 10 }}>
                      {concurrentUsersRegistered} / {licence.concurrentUsers}
                    </Typography>
                    <Typography variant="h5" color="textSecondary">
                      {t("concurrent_users")}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h4" style={{ marginBottom: 10 }}>
                      {licence.usedLimitedUsers} / {licence.limitedUsers}
                    </Typography>
                    <Typography variant="h5" color="textSecondary">
                      {t("limited_user_plural")}
                    </Typography>
                  </CardContent>
                </Card>
              </div>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h5" color="textSecondary">
                      {t("global_document_plural") + " " + t("global_used")}
                    </Typography>
                  </CardContent>
                </Card>
                <Card variant="outlined" style={{ margin: 10 }}>
                  <CardContent style={{ textAlign: "center", padding: 50 }}>
                    <Typography variant="h4" style={{ marginBottom: 10 }}>
                      {licence.usedDocuments} / {licence.documents}
                    </Typography>
                    <Typography variant="h5" color="textSecondary">
                      {t("global_document_plural")}
                    </Typography>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : (
            <div style={{ width: "100%" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Card raised style={{ padding: 30, textAlign: "center" }}>
                  <Typography variant="h5">{t("no_licence_found")}</Typography>
                  <Typography>{t("no_licence_found_not_licenced")}</Typography>
                </Card>
              </div>
            </div>
          )}
          {allLicences &&
            allLicences.length > 0 && (
              <div style={{ margin: 15 }}>
                <Accordion classes={{ root: classes.accordionRoot }}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    classes={{ root: classes.accordionSummaryRoot }}
                  >
                    <Typography className={classes.heading}>
                      {t("licence_details_header")} ({allLicences.length}{" "}
                      {t("registered_licence")}
                      {allLicences.length > 1 && "s"})
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t("licence_key")}</TableCell>
                            <TableCell>{t("licence_description")}</TableCell>
                            <TableCell>{t("global_user_plural")}</TableCell>
                            <TableCell>{t("concurrent_users")}</TableCell>
                            <TableCell>{t("limited_user_plural")}</TableCell>
                            <TableCell>{t("global_document_plural")}</TableCell>
                            <TableCell>{t("licence_expiry_date")}</TableCell>
                            <TableCell>{t("global_actions")}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {allLicences.map((row) => {
                            return (
                              <TableRow key={row.key}>
                                <TableCell className={classes.keyColumn}>
                                  {row.key}
                                </TableCell>
                                <TableCell
                                  className={classes.descriptionColumn}
                                >
                                  {row.description}
                                </TableCell>
                                <TableCell>{row.namedUsers}</TableCell>
                                <TableCell>{row.concurrentUsers}</TableCell>
                                <TableCell>{row.limitedUsers}</TableCell>
                                <TableCell>{row.documents}</TableCell>
                                <TableCell>
                                  {moment(row.expiryDate).format(
                                    "DD/MM/YYYY HH:mm:ss"
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Tooltip title={t("delete_licence_tooltip")}>
                                    <IconButton
                                      disabled={allLicences.length === 1}
                                      size="small"
                                      onClick={() => {
                                        setConfirmDeleteDialogOpen(true);
                                        setToDelete(row);
                                      }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          <TableRow>
                            <TableCell>
                              <b>{t("total_licence_limits")}</b>
                            </TableCell>
                            <TableCell />
                            <TableCell>{licence.namedUsers}</TableCell>
                            <TableCell>{licence.concurrentUsers}</TableCell>
                            <TableCell>{licence.limitedUsers}</TableCell>
                            <TableCell>{licence.documents}</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              </div>
            )}
        </div>
      </>
      <Dialog
        fullWidth
        open={confirmLicenseDialogOpen}
        onClose={() => setConfirmLicenseDialogOpen(false)}
      >
        <DialogTitle>{t("license_organization_title")}</DialogTitle>
        <DialogContent>{t("confirm_license_organization_body")}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmLicenseDialogOpen(false)}
            color="primary"
          >
            {t("global_cancel")}
          </Button>
          <Button onClick={() => licenseOrganization()} color="primary">
            {t("add_licence_action")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        fullWidth
        open={confirmDeleteDialogOpen}
        onClose={() => setConfirmDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("global_are_you_sure")}</DialogTitle>
        <DialogContent>{t("confirm_licence_delete_body")}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => setConfirmDeleteDialogOpen(false)}
            color="primary"
          >
            {t("global_cancel")}
          </Button>
          <Button onClick={() => deleteLicence()} color="primary">
            {t("global_delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default LicenceSettings;
